import {
  useQuery,
  type QueryHookOptions,
  useMutation,
  type MutationHookOptions,
  useLazyQuery,
} from "@apollo/client";

import { humanize } from "../../formats/common";
import { formatTenantName } from "../../formats/tenants";
import {
  TenantsDocument,
  type TenantsQuery,
  type TenantsQueryVariables,
  TenantOptionsDocument,
  type TenantOptionsQuery,
  type TenantOptionsQueryVariables,
  type TenantUpdateMutation,
  type TenantUpdateMutationVariables,
  TenantUpdateDocument,
  type TenantCreateMutation,
  type TenantCreateMutationVariables,
  TenantCreateDocument,
  type TenantDiscardMutation,
  type TenantDiscardMutationVariables,
  TenantDiscardDocument,
  type TenantQuery,
  type TenantQueryVariables,
  TenantDocument,
  SortDirection,
  type TenantStatsQuery,
  type TenantStatsQueryVariables,
  TenantStatsDocument,
  TenantCloneDocument,
  type TenantCloneMutation,
  type TenantCloneMutationVariables,
  type GenerateBillingPortalLinkMutation,
  type GenerateBillingPortalLinkMutationVariables,
  GenerateBillingPortalLinkDocument,
  type SubscriptionCreateMutation,
  type SubscriptionCreateMutationVariables,
  SubscriptionCreateDocument,
  type SubscriptionPlanEnumTypeQuery,
  type SubscriptionPlanEnumTypeQueryVariables,
  SubscriptionPlanEnumTypeDocument,
  type StripeCustomerCreateMutation,
  type StripeCustomerCreateMutationVariables,
  StripeCustomerCreateDocument,
  type WorkOrderQuotaQuery,
  type WorkOrderQuotaQueryVariables,
  WorkOrderQuotaDocument,
} from "../../graphql";
import { useCurrentUser } from "./users";

export function useTenantOptions() {
  const [load, { loading, data }] = useLazyQuery<
    TenantOptionsQuery,
    TenantOptionsQueryVariables
  >(TenantOptionsDocument, {
    variables: { sort: { priority: SortDirection.Asc } },
  });

  const search = (val?: string) => {
    load({
      variables: {
        filter: val ? { fullName: { match: val } } : undefined,
        sort: val ? undefined : { priority: SortDirection.Asc },
      },
    });
  };

  const options = (data?.tenants?.items || []).map((t) => ({
    key: t.id,
    label: formatTenantName(t),
    tenant: t,
  }));

  return { load, search, loading, options };
}

export function useTenants(
  options?: QueryHookOptions<TenantsQuery, TenantsQueryVariables>
) {
  const { loading, data } = useQuery(TenantsDocument, options);

  if (!data || !data.tenants || !data.tenants.items) {
    return { loading };
  }

  const items = data.tenants.items;
  const totalCount = data.tenants.totalCount;

  return { loading, items, totalCount };
}

export function useLazyTenants() {
  return useLazyQuery<TenantsQuery, TenantsQueryVariables>(TenantsDocument, {
    notifyOnNetworkStatusChange: true,
  });
}

export function useTenantStats(
  options?: QueryHookOptions<TenantStatsQuery, TenantStatsQueryVariables>
) {
  const { loading, data } = useQuery(TenantStatsDocument, options);

  if (!data || !data.tenants || !data.tenants.items) {
    return { loading };
  }

  const items = data.tenants.items;
  const totalCount = data.tenants.totalCount;

  return { loading, items, totalCount };
}

export function useTenant({
  id,
  accountingSettings = false,
  withLocalities = true,
  bankAccounts = false,
}: {
  id: string;
  accountingSettings?: boolean;
  withLocalities?: boolean;
  bankAccounts?: boolean;
}) {
  const { data } = useQuery<TenantQuery, TenantQueryVariables>(TenantDocument, {
    variables: { id, accountingSettings, withLocalities, bankAccounts },
    skip: !id,
  });

  return data?.tenant;
}

export function useRefetchTenant(id: string) {
  const { data, refetch, loading } = useQuery<
    TenantQuery,
    TenantQueryVariables
  >(TenantDocument, {
    variables: { id },
  });

  return { tenant: data?.tenant, refetchTenant: refetch, loading };
}

export function useTenantUpdate(
  options?: MutationHookOptions<
    TenantUpdateMutation,
    TenantUpdateMutationVariables
  >
) {
  return useMutation(TenantUpdateDocument, options);
}

export function useTenantCreate(
  options?: MutationHookOptions<
    TenantCreateMutation,
    TenantCreateMutationVariables
  >
) {
  return useMutation(TenantCreateDocument, options);
}

export function useTenantDiscard(
  options?: MutationHookOptions<
    TenantDiscardMutation,
    TenantDiscardMutationVariables
  >
) {
  return useMutation(TenantDiscardDocument, options);
}

export function useTenantClone(
  options?: MutationHookOptions<
    TenantCloneMutation,
    TenantCloneMutationVariables
  >
) {
  return useMutation(TenantCloneDocument, options);
}

export function useGenerateBillingLink(
  options?: MutationHookOptions<
    GenerateBillingPortalLinkMutation,
    GenerateBillingPortalLinkMutationVariables
  >
) {
  return useMutation(GenerateBillingPortalLinkDocument, options);
}

export function useSubscriptionCreate(
  options?: MutationHookOptions<
    SubscriptionCreateMutation,
    SubscriptionCreateMutationVariables
  >
) {
  return useMutation(SubscriptionCreateDocument, options);
}

export function useSubscriptionPlanOptions() {
  const [load, { loading, data }] = useLazyQuery<
    SubscriptionPlanEnumTypeQuery,
    SubscriptionPlanEnumTypeQueryVariables
  >(SubscriptionPlanEnumTypeDocument);

  const options = data?.__type?.enumValues?.map((s) => ({
    key: s.name,
    value: s.name,
    label: humanize(s.name),
  }));

  return { load, loading, options };
}

export function useStripeCustomerCreate(
  options?: MutationHookOptions<
    StripeCustomerCreateMutation,
    StripeCustomerCreateMutationVariables
  >
) {
  return useMutation(StripeCustomerCreateDocument, options);
}

export function useWorkOrderQuota() {
  const { currentTenant } = useCurrentUser();
  const limit = currentTenant.woLimit;
  const { data, loading } = useQuery<
    WorkOrderQuotaQuery,
    WorkOrderQuotaQueryVariables
  >(WorkOrderQuotaDocument, {
    skip: !limit,
    fetchPolicy: "cache-and-network",
  });
  const count =
    !limit || loading ? undefined : data?.me?.currentTenant?.woCount;

  return {
    limit,
    count,
    loading,
    reached: !!limit && count !== undefined && count >= limit,
  };
}
